/**
 * Find a room.
 *
 * The whole point of the portal. Changing the time never loses the rest of
 * the search, unavailable rooms say why rather than failing on submit, and a
 * room that needs approval says so before the form is opened.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { BookingForm } from "@/pages/portal/BookingForm";
import { RoomCard } from "@/pages/portal/RoomCard";
import { RoomDetail } from "@/pages/portal/RoomDetail";
import { Icon } from "@/components/Icon";
import { EmptyState, ErrorState, Notice, SkeletonCards } from "@/components/States";
import { api } from "@/lib/api";
import { addMinutes, formatClock, formatLongDate, isoDate, nextSlot } from "@/lib/format";
import type { LocationRecord, SearchRoom } from "@/lib/types";

const DURATIONS = [30, 45, 60, 90, 120, 180];

interface SearchState {
  date: string;
  time: string;
  duration: number;
  locationId: string;
  floor: string;
  capacity: string;
  amenities: string[];
  query: string;
}

function initialState(): SearchState {
  const start = nextSlot(15);
  return {
    date: isoDate(start),
    time: `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`,
    duration: 60,
    locationId: "",
    floor: "",
    capacity: "",
    amenities: [],
    query: "",
  };
}

export function RoomSearch() {
  const [search, setSearch] = useState<SearchState>(initialState);
  const [bookingRoom, setBookingRoom] = useState<SearchRoom | null>(null);
  const [detailRoomId, setDetailRoomId] = useState<string | null>(null);

  const window = useMemo(() => {
    const start = new Date(`${search.date}T${search.time}:00`);
    return { start, end: addMinutes(start, search.duration) };
  }, [search.date, search.time, search.duration]);

  const { data: facets } = useQuery({
    queryKey: ["portal-facets"],
    queryFn: () =>
      api.get<{ locations: LocationRecord[]; floors: string[]; amenities: string[] }>(
        "/portal/locations",
      ),
    staleTime: 300_000,
  });

  const roomsQuery = useQuery({
    queryKey: ["portal-rooms", window.start.toISOString(), window.end.toISOString(),
      search.locationId, search.floor, search.capacity, search.amenities.join(","), search.query],
    queryFn: () =>
      api.get<{ rooms: SearchRoom[]; count: number; available_count: number }>(
        "/portal/rooms",
        {
          start_at: window.start.toISOString(),
          end_at: window.end.toISOString(),
          location_id: search.locationId || undefined,
          floor: search.floor || undefined,
          min_capacity: search.capacity || undefined,
          amenities: search.amenities.join(",") || undefined,
          q: search.query || undefined,
        },
      ),
  });

  function update<K extends keyof SearchState>(key: K, value: SearchState[K]) {
    setSearch((current) => ({ ...current, [key]: value }));
  }

  function toggleAmenity(amenity: string) {
    setSearch((current) => ({
      ...current,
      amenities: current.amenities.includes(amenity)
        ? current.amenities.filter((item) => item !== amenity)
        : [...current.amenities, amenity],
    }));
  }

  const rooms = roomsQuery.data?.rooms ?? [];
  const available = rooms.filter((room) => room.available);
  const unavailable = rooms.filter((room) => !room.available);
  const hasFilters =
    Boolean(search.locationId || search.floor || search.capacity || search.query) ||
    search.amenities.length > 0;

  return (
    <div className="page">
      <div className="page__head">
        <div className="page__title">
          <h1 className="t-h1">Find a room</h1>
          <p className="t-body ink-secondary">
            {formatLongDate(window.start.toISOString())} ·{" "}
            {formatClock(window.start)}
            –{formatClock(window.end)}
          </p>
        </div>
      </div>

      <section className="searchbar" aria-label="Search for a room">
        <div className="field">
          <label className="field__label" htmlFor="date">Date</label>
          <input id="date" className="input" type="date" value={search.date}
            onChange={(event) => update("date", event.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="time">From</label>
          <input id="time" className="input" type="time" step={900} value={search.time}
            onChange={(event) => update("time", event.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="duration">For</label>
          <select id="duration" className="select" value={search.duration}
            onChange={(event) => update("duration", Number(event.target.value))}>
            {DURATIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes < 60 ? `${minutes} minutes` : minutes === 60 ? "1 hour" : `${minutes / 60} hours`}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="location">Location</label>
          <select id="location" className="select" value={search.locationId}
            onChange={(event) => update("locationId", event.target.value)}>
            <option value="">All locations</option>
            {facets?.locations.map((location) => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="capacity">Seats</label>
          <select id="capacity" className="select" value={search.capacity}
            onChange={(event) => update("capacity", event.target.value)}>
            <option value="">Any size</option>
            {[2, 4, 6, 8, 12, 16, 20].map((size) => (
              <option key={size} value={size}>{size}+ people</option>
            ))}
          </select>
        </div>
        <div className="field searchbar__wide">
          <label className="field__label" htmlFor="q">Room name</label>
          <input id="q" className="input" type="search" placeholder="Search rooms"
            value={search.query} onChange={(event) => update("query", event.target.value)} />
        </div>
      </section>

      {Boolean(facets?.amenities.length) && (
        <div className="row" style={{ gap: "var(--space-3)", marginTop: "var(--space-4)", flexWrap: "wrap" }}>
          <span className="t-label ink-tertiary">Needs</span>
          <div className="chips">
            {facets!.amenities.map((amenity) => (
              <button key={amenity} type="button" className="chip"
                aria-pressed={search.amenities.includes(amenity)}
                onClick={() => toggleAmenity(amenity)}>
                {amenity}
              </button>
            ))}
            {hasFilters && (
              <button type="button" className="chip" onClick={() => setSearch(initialState)}>
                <Icon name="x" size={14} />
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: "var(--space-6)" }}>
        {roomsQuery.isPending ? (
          <SkeletonCards count={6} />
        ) : roomsQuery.isError ? (
          <ErrorState error={roomsQuery.error} onRetry={() => roomsQuery.refetch()} />
        ) : rooms.length === 0 ? (
          <EmptyState
            icon="door"
            title="No rooms match this search"
            message={
              hasFilters
                ? "Widen the filters, or try a different time — the rest of your search is kept."
                : "No rooms have been set up yet. An administrator can add them in the admin portal."
            }
            action={
              hasFilters ? (
                <button className="btn btn--secondary" onClick={() => setSearch(initialState)}>
                  Clear filters
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="stack" style={{ gap: "var(--space-6)" }}>
            {available.length > 0 ? (
              <section aria-labelledby="available-heading" className="stack" style={{ gap: "var(--space-3)" }}>
                <h2 className="t-h3" id="available-heading">
                  {available.length} {available.length === 1 ? "room is" : "rooms are"} free
                </h2>
                <div className="room-grid">
                  {available.map((room) => (
                    <RoomCard key={room.id} room={room}
                      onBook={() => setBookingRoom(room)}
                      onOpen={() => setDetailRoomId(room.id)}
                      window={window} />
                  ))}
                </div>
              </section>
            ) : (
              <Notice tone="warning" title="Nothing is free at this time">
                Every room is taken for {formatClock(window.start)}
                –{formatClock(window.end)}. Try a
                shorter meeting or a later start — the rooms below show when they free up.
              </Notice>
            )}

            {unavailable.length > 0 && (
              <section aria-labelledby="unavailable-heading" className="stack" style={{ gap: "var(--space-3)" }}>
                <h2 className="t-h3 ink-secondary" id="unavailable-heading">
                  Not available for this time
                </h2>
                <div className="room-grid">
                  {unavailable.map((room) => (
                    <RoomCard key={room.id} room={room}
                      onOpen={() => setDetailRoomId(room.id)} window={window} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {bookingRoom && (
        <BookingForm
          room={bookingRoom}
          start={window.start}
          durationMinutes={search.duration}
          onClose={() => setBookingRoom(null)}
          onBooked={() => {
            setBookingRoom(null);
            void roomsQuery.refetch();
          }}
        />
      )}

      {detailRoomId && (
        <RoomDetail
          roomId={detailRoomId}
          date={search.date}
          onClose={() => setDetailRoomId(null)}
          onBook={(room) => {
            setDetailRoomId(null);
            setBookingRoom(room);
          }}
        />
      )}
    </div>
  );
}
